import React, { useRef, useState } from 'react'
import { Breadcrumb, Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'
import {
  CreateDocumentTypePayload,
  UpdateDocumentTypePayload,
  useCreateDocumentTypeMutation,
  useUpdateDocumentTypeMutation,
} from '../../../store/ai/documentTypes'

import { DocumentTypeCurrentUserTemplateInputParameterConfigForm } from '../../../components/InputParameterHierarchy/Template/DocumentTypeCurrentUserTemplateInputParameterConfigForm'
import { DocumentTypeTeamMemberTemplateInputParameterConfigForm } from '../../../components/InputParameterHierarchy/Template/DocumentTypeTeamMemberTemplateInputParameterConfigForm'
import { DocumentTypeGlobalTemplateInputParameterConfigForm } from '../../../components/InputParameterHierarchy/Template/DocumentTypeGlobalTemplateInputParameterConfigForm'
import { DocumentTypeInputParametersHierarchyConfigModal } from '../../../components/InputParameterHierarchy/DocumentTypeInputParametersHierarchyConfigModal'
import { InputParameterHierarchyType } from '../../../components/InputParameterHierarchy/model'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import buttonStyles from '../../../components/Button/Button.module.scss'
import { Button } from '../../../components/Button/Button'
import { TagElement } from '../../../components/Tag/Tag'

import { DocumentType } from '../../../models/DocumentType'

import { UpdateSystemDocumentTypeModal } from './UpdateSystemDocumentTypeModal'
import { useCopyDocumentTemplate, useDocumentType } from './hooks'

import './DocumentTypes.scss'

interface FormValues {
  name: string
  description: string
  documentLabels: string[]
}

const documentTypeToFormValues = (documentType: DocumentType | null, copied: boolean) => {
  if (!documentType) {
    return { name: 'New Document Type', description: '', documentLabels: [] }
  }
  const { name, detectionConfig } = documentType
  const { description, documentLabels } = detectionConfig
  return { name: copied ? `Copy of ${name}` : name, description, documentLabels }
}

const formDataToCreateDocumentTypePayload = (formData: FormValues, newName?: string): CreateDocumentTypePayload => {
  const { name, description, documentLabels } = formData
  const detectionConfig = { description, documentLabels }
  return { name: newName ?? name, detectionConfig }
}

const formDataToUpdateDocumentTypePayload = (
  formData: FormValues,
  dirtyFields: Partial<Record<keyof FormValues, boolean | boolean[] | undefined>>,
): UpdateDocumentTypePayload => {
  const { name: isNameDirty, description: isDescriptionDirty, documentLabels: isDocumentLabelsDirty } = dirtyFields
  const { name, description, documentLabels } = formData
  return {
    ...(isNameDirty && { name }),
    ...((isDescriptionDirty || isDocumentLabelsDirty) && {
      detectionConfig: {
        description,
        documentLabels,
      },
    }),
  }
}

export const DocumentTypeView: React.FC = () => {
  const navigate = useNavigate()
  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)

  const { operation = null, documentTypeId = '' } = useParams()

  const copied = operation === 'copy'

  const [triggerUpdateDocumentType, { isLoading: documentTypeUpdateIsLoading }] = useUpdateDocumentTypeMutation()
  const [triggerCreateDocumentType] = useCreateDocumentTypeMutation()

  const [showUpdateSystemModal, setShowUpdateSystemModal] = useState<boolean>(false)
  const [showIpHierarchyConfigModal, setShowIpHierarchyConfigModal] = useState<boolean>(false)
  const [documentTypeCreateIsLoading, setDocumentTypeCreateIsLoading] = useState<boolean>(false)

  const { documentType, fetchDocumentType, isLoading: documentTypeLoading } = useDocumentType(documentTypeId)
  const { copyTemplates, ipHierarchyDefault } = useCopyDocumentTemplate({ documentType })

  const getDefaultValues = async () => documentTypeToFormValues(await fetchDocumentType(), copied)

  const form = useForm<FormValues>({ defaultValues: getDefaultValues })

  const {
    formState: { isDirty, dirtyFields },
    handleSubmit,
    watch,
    control,
  } = form

  const name = watch('name')

  const isLoading = documentTypeLoading || documentTypeCreateIsLoading || documentTypeUpdateIsLoading
  const fromSystemDocumentType = !!documentType?.systemDocumentTypeId
  const createNewDocumentTypeFromSystem =
    fromSystemDocumentType && (!isDirty || !!dirtyFields.name || !!dirtyFields.description || !!dirtyFields.documentLabels)

  const handleIpHierarchyConfigModalHide = () => {
    setShowIpHierarchyConfigModal(false)
  }

  const handleCancel = () => {
    navigate('/workspace/setup/document-types')
  }

  const handleSaveButtonClicked = async () => {
    if (createNewDocumentTypeFromSystem && !copied) {
      setShowUpdateSystemModal(true)
    } else {
      const onSubmit = (formData: FormValues) => handleSaveDocumentType(formData)
      await handleSubmit(onSubmit)()
    }
  }

  const handleUpdateDocumentType = async (formData: FormValues) => {
    try {
      const payload = formDataToUpdateDocumentTypePayload(formData, dirtyFields)
      await triggerUpdateDocumentType({ accountId, documentTypeId, payload }).unwrap()
      navigate('/workspace/setup/document-types')
    } catch (e) {
      console.error(e)
    }
  }

  const handleCreateDocumentType = async (formData: FormValues, newName?: string) => {
    setDocumentTypeCreateIsLoading(true)
    try {
      const payload = formDataToCreateDocumentTypePayload(formData, newName)
      const newDocumentType = await triggerCreateDocumentType({ accountId, payload }).unwrap()
      if (copied) {
        await copyTemplates(newDocumentType.id)
      }
      navigate('/workspace/setup/document-types')
    } catch (e) {
      console.error(e)
    } finally {
      setDocumentTypeCreateIsLoading(false)
    }
  }

  const _handleSaveDocumentType = async (formData: FormValues, newName?: string) => {
    if (!documentTypeId || copied || createNewDocumentTypeFromSystem) {
      await handleCreateDocumentType(formData, newName)
      return
    }
    await handleUpdateDocumentType(formData)
  }

  const handleSaveDocumentType = tracked(
    _handleSaveDocumentType,
    {
      eventName: documentTypeId
        ? createNewDocumentTypeFromSystem
          ? TrackEvent.DOCUMENT_TYPE_CREATED_FROM_SYSTEM
          : copied
            ? TrackEvent.DOCUMENT_TYPE_COPIED
            : TrackEvent.DOCUMENT_TYPE_UPDATED
        : TrackEvent.DOCUMENT_TYPE_CREATED,
    },
    {
      eventDetailsExtractor: (formData: FormValues, newDocumentTypeName?: string) => {
        return newDocumentTypeName ? { name: newDocumentTypeName } : {}
      },
    },
  )

  const handleConfirm = async (newName?: string) => {
    const onSubmit = (formData: FormValues) => handleSaveDocumentType(formData, newName)
    await handleSubmit(onSubmit)()
  }

  return (
    <FormProvider {...form}>
      {showIpHierarchyConfigModal && documentType && ipHierarchyDefault && (
        <DocumentTypeInputParametersHierarchyConfigModal
          show={showIpHierarchyConfigModal}
          onHide={handleIpHierarchyConfigModalHide}
          type={InputParameterHierarchyType.DOCUMENT_SUMMARY_TEMPLATE}
          typeId='summary-template'
          name='Document Summary Template'
          inputParametersSchema={{
            properties: {
              template: {
                type: 'string',
                description: 'Document Summary Template',
              },
            },
          }}
          defaultInputParameters={ipHierarchyDefault.inputParameters}
          documentTypeId={documentType.id}
          DocumentTypeConfig={DocumentTypeGlobalTemplateInputParameterConfigForm}
          CurrentUserConfig={DocumentTypeCurrentUserTemplateInputParameterConfigForm}
          TeamMembersConfig={DocumentTypeTeamMemberTemplateInputParameterConfigForm}
        />
      )}
      {showUpdateSystemModal && (
        <UpdateSystemDocumentTypeModal
          show={showUpdateSystemModal}
          onCancel={() => setShowUpdateSystemModal(false)}
          onConfirm={handleConfirm}
          documentTypeName={name}
        />
      )}
      <Container className='d-flex flex-column zocks-gap-1'>
        {isLoading && (
          <div className='d-flex align-items-center justify-content-center'>
            <Spinner />
          </div>
        )}
        {!isLoading && (
          <>
            <Row>
              <Col className='p-0'>
                <Breadcrumb>
                  <Breadcrumb.Item
                    linkProps={{
                      to: '/workspace/setup/document-types',
                      className: `${buttonStyles.button} ${buttonStyles.linkButton} text-decoration-underline`,
                    }}
                    linkAs={NavLink}
                  >
                    Document Types
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active>{name}</Breadcrumb.Item>
                </Breadcrumb>
              </Col>
            </Row>
            {documentTypeId && fromSystemDocumentType ? (
              <Row>
                <Col className='p-0 d-flex gap-2'>
                  <TagElement>System</TagElement>
                </Col>
              </Row>
            ) : null}
            <Row>
              <Col className='p-0'>
                <Form.Label className='label-medium'>Document type name</Form.Label>
                <Controller
                  name='name'
                  control={control}
                  render={({ field }) => <Form.Control type='text' value={field.value} onChange={field.onChange} placeholder='Document type name' />}
                />
              </Col>
            </Row>
            <Row>
              <Col className='p-0'>
                <h5>Configuration</h5>
              </Col>
            </Row>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'document_type:update', superScope: ['document_type:admin'] }}>
              <Row>
                <Col className='p-0'>
                  <div className='d-flex gap-2'>
                    <Form.Label className='paragraph-medium'>Description</Form.Label>
                  </div>
                  <div className='flex-grow-1 d-flex zocks-form-container'>
                    <Controller
                      name='description'
                      control={control}
                      render={({ field }) => (
                        <Form.Control as='textarea' rows={4} value={field.value} onChange={field.onChange} placeholder='Document type description' />
                      )}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col className='d-flex flex-column zocks-gap-0.5 p-0'>
                  <div className='d-flex flex-column'>
                    <Form.Label className='paragraph-medium'>Document labels</Form.Label>
                    <span className='paragraph-small'>List the document types the AI Assistant should apply this template to. Press Enter after typing.</span>
                  </div>
                  <DocumentLabelInput />
                </Col>
              </Row>
            </ScopedBlock>
            <Row>
              <Col className='p-0 d-flex flex-column gap-3'>
                <h5>Configure Summary Templates</h5>
                <div className='d-flex flex-wrap gap-4'>
                  <Button variant='primaryGhost' onClick={() => setShowIpHierarchyConfigModal(true)} disabled={copied || !documentType}>
                    <i className='bi bi-gear pe-2' />
                    Summary Template
                  </Button>
                </div>
              </Col>
            </Row>
            <Row>
              <Col className='d-flex align-items-center p-0 mt-4 justify-content-lg-end gap-3'>
                <Button variant='secondary' onClick={handleCancel}>
                  Cancel
                </Button>
                <ScopedBlock
                  missingScopeAction={MissingScopeAction.DISABLE}
                  scope={{
                    scope: documentTypeId ? 'document_type:update' : 'document_type:create',
                    superScope: ['document_type:admin'],
                  }}
                >
                  <Button variant='primary' onClick={handleSaveButtonClicked} disabled={!name || (!fromSystemDocumentType && !copied && !isDirty)}>
                    <i className='bi bi-check-lg' />
                    Save
                  </Button>
                </ScopedBlock>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </FormProvider>
  )
}

const DocumentLabelInput = () => {
  const { watch, setValue } = useFormContext<FormValues>()

  const documentLabels = watch('documentLabels') ?? []

  const [newDocumentLabel, setNewDocumentLabel] = useState<string | null>(null)

  const inputRef = useRef<HTMLInputElement>(null)

  const removeDocumentLabel = (index: number) => {
    const filteredDocumentLabels = documentLabels.filter((_, i) => i !== index)
    setValue('documentLabels', filteredDocumentLabels)
  }

  const handleClick = () => {
    if (!inputRef.current) {
      return
    }
    inputRef.current.focus()
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewDocumentLabel(e.target.value)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' || !newDocumentLabel) {
      return
    }
    setValue('documentLabels', [...documentLabels, newDocumentLabel])
    setNewDocumentLabel(null)
  }

  return (
    <div
      className='d-flex align-items-center flex-wrap zocks-gap-0.625 p-2 border rounded-1 zocks-border-color-gray-500 document-label-input-container'
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {documentLabels.map((documentLabel, index) => {
        const handleClick = () => {
          removeDocumentLabel(index)
        }
        return (
          <div key={`document-label-${index}`} className='d-flex align-items-center gap-2 border rounded-1 zocks-bg-color-gray-200 px-2 py-1'>
            <span>{documentLabel}</span>
            <i className='bi bi-x fs-6 cursor-pointer' onClick={handleClick} />
          </div>
        )
      })}
      <Form.Control ref={inputRef} type='text' className='document-label-input border-0' value={newDocumentLabel ?? ''} onChange={handleChange} />
    </div>
  )
}
